import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subscription, filter, map } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogState } from "@angular/material/dialog";
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";

import { ENV } from "../../../../../app/app.runtime";
import { subscriptionNameKey } from "../../../../../helpers/subscription-i18n";
import { AuthService } from "../../../../../services/authservice";
import { ChooseCustomerDialog } from "../../../../dialogs/choose-customer-dialog/choose-customer-dialog";
import { DialogGenericComponent } from "../../../../dialogs/dialog-generic/dialog-generic";
import { DialogRoles } from "../../../../dialogs/dialog-roles/dialog-roles";
import { CustomerDisplay } from "../../../customer-display/customer-display";
import { ProfileMenuComponent } from "../../../profile-menu/profile-menu.component";

@Component({
  selector: "navigation-topbar",
  templateUrl: "navigation-topbar.html",
  styleUrls: ["../../navigation.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CustomerDisplay, RouterLink, RouterLinkActive, TranslatePipe, ProfileMenuComponent],
})
export class NavigationTopbarComponent {
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private translate = inject(TranslateService);

  protected userCanStartSelfservice = input.required<boolean>();

  protected newsUrl = ENV.NWD_NEWS_URL;
  protected viewingCustomer = computed(() => this.auth.viewingCustomer());
  protected allowSwitch = computed(() => this.auth.isSuperUserRO() || this.auth.customers().length > 1);

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected isServicesRoute = computed(() => {
    const url = this.currentUrl();
    return (
      url === "/"
      || url.startsWith("/diensten/")
      || url.startsWith("/subscription/")
      || url.startsWith("/ip-prefixes")
      || url.startsWith("/cert")
      || url.startsWith("/domains")
    );
  });
  private roleEventSubscription: Subscription = Subscription.EMPTY;

  constructor() {
    if (this.roleEventSubscription.closed) {
      this.roleEventSubscription = this.auth.roleEvent.subscribe((roleEvent) => {
        const organizationName = this.auth.currentUser().organizationName;

        switch (roleEvent.event) {
          case "no-roles":
            this.openRolesDialog(
              this.translate.instant("Role.NoRoles.Title", {
                organisation: organizationName,
              }),
            );
            break;
          case "no-organization":
            const params = {
              data: {
                title: this.translate.instant("Dialog.OrganizationUnknown.Title"),
                message: this.translate.instant("Dialog.OrganizationUnknown.Message"),
                state: {},
                code: null,
              },
            };
            this.dialog.open(DialogGenericComponent, params);
            break;
          case "requirements-not-met": {
            const productType = roleEvent.productType ?? "";
            const nameKey = subscriptionNameKey(productType);
            this.openRolesDialog(
              this.translate.instant("Role.NoAccess.Title", {
                productType: nameKey ? this.translate.instant(nameKey) : productType,
              }),
              roleEvent.need,
            );
            break;
          }
        }
      });
    }
  }

  protected logOut() {
    this.auth.logout();
  }

  openChooserDialog() {
    this.dialog.open(ChooseCustomerDialog, {});
  }

  openRolesDialog(dialogHeader?: string, need: string[] = []) {
    const organizationName = this.auth.currentUser().organizationName;
    const roles = this.auth.currentUser().roles ?? [];
    const defaultHeader = this.translate.instant("Role.ViewRoles.Title", {
      organisation: organizationName,
    });

    const openDialog = this.dialog.getDialogById("roles-dialog");
    if (openDialog?.getState() !== MatDialogState.OPEN) {
      this.dialog.open(DialogRoles, {
        id: "roles-dialog",
        data: {
          roles,
          title: this.translate.instant("Navigation.Roles"),
          description: dialogHeader || defaultHeader,
          need,
        },
      });
    }
  }
}
